/**
 * ShapeNode - SVG shapes (rectangle, circle, triangle) for whiteboard
 *
 * Features:
 * - SVG rendering for crisp scaling at any zoom level
 * - NodeResizer for resize handles
 * - Handles on all sides for connections
 * - Color customization (via property panel in Plan 05)
 *
 * Why SVG?
 * - SVG scales without pixelation (important for zoom)
 * - Easy to calculate points for shapes like triangles
 * - Built-in fill and stroke properties
 */
import { memo } from 'react';
import { Handle, Position, NodeResizer, type NodeProps, type Node } from '@xyflow/react';

// ============================================================================
// SHAPE TYPE DEFINITIONS
// ============================================================================

/**
 * ShapeType - Available shape types
 *
 * These are the three basic shapes for diagramming:
 * - rectangle: Most common, boxes in flowcharts
 * - circle: Decision points, cycles
 * - triangle: Pointers, directions
 */
export type ShapeType = 'rectangle' | 'circle' | 'triangle';

/**
 * ShapeData - Data shape stored in Y.Map for shape nodes
 *
 * @property shapeType - Which shape to render (rectangle, circle, triangle)
 * @property color - Fill color for the shape
 * @property width - SVG width in pixels
 * @property height - SVG height in pixels
 * @property strokeColor - Optional border color (defaults to selection indicator)
 * @property strokeWidth - Optional border width (defaults to 2)
 * @property aiGenerated - Optional flag indicating this node was created by AI
 * @property generatedAt - Optional ISO timestamp of when AI created this node
 * @property model - Optional AI model name that created this node
 *
 * Note: Index signature required for React Flow Node<T> type compatibility
 */
export interface ShapeData {
  shapeType: ShapeType;
  color: string;
  width: number;
  height: number;
  strokeColor?: string;
  strokeWidth?: number;
  // Text label (optional - centered in shape)
  content?: string;
  // AI attribution properties
  aiGenerated?: boolean;
  generatedAt?: string;
  model?: string;
  // Index signature for React Flow compatibility
  [key: string]: unknown;
}

/**
 * ShapeNodeType - Full node type for type-safe React Flow usage
 */
export type ShapeNodeType = Node<ShapeData, 'shape'>;

// ============================================================================
// SHAPE COLORS
// ============================================================================

/**
 * SHAPE_COLORS - Material Design 300-level colors
 *
 * Slightly more saturated than sticky note colors.
 * These work well as shape fills with the default stroke.
 */
export const SHAPE_COLORS = {
  blue: '#93c5fd',    // Blue 300 - Default shape color
  green: '#86efac',   // Green 300
  red: '#fca5a5',     // Red 300
  yellow: '#fde047',  // Yellow 300
  purple: '#c4b5fd',  // Purple 300
  gray: '#d1d5db',    // Gray 300
} as const;

// ============================================================================
// SHAPE NODE COMPONENT
// ============================================================================

/**
 * ShapeNode - The SVG shape component
 *
 * Rendering behavior:
 * - Rectangle: Rounded rect with rx=4 for smooth corners
 * - Circle: Ellipse that fits the bounding box
 * - Triangle: Polygon points calculated from width/height
 *
 * Selection feedback:
 * - When selected, stroke becomes blue (#3b82f6)
 * - Stroke width increases slightly for visibility
 */
export const ShapeNode = memo(({ data, selected, dragging }: NodeProps<ShapeNodeType>) => {
  // Destructure with defaults
  const {
    shapeType,
    color,
    width = 100,
    height = 100,
    strokeColor,
    strokeWidth = 2,
  } = data;

  // ---------------------------------------------------------------------------
  // DYNAMIC SHADOW based on node state
  // ---------------------------------------------------------------------------
  const getShadowStyle = () => {
    if (dragging) {
      return 'var(--shadow-elevation-high)';
    }
    if (selected) {
      return 'var(--shadow-elevation-medium)';
    }
    return 'var(--shadow-elevation-low)';
  };

  // ---------------------------------------------------------------------------
  // SHAPE RENDERING FUNCTIONS
  // ---------------------------------------------------------------------------

  /**
   * renderShape - Returns the appropriate SVG element for the shape type
   *
   * Stroke behavior:
   * - If strokeColor is provided, use it
   * - If selected, use blue (#3b82f6) for visibility
   * - Otherwise, transparent (no visible border)
   */
  const renderShape = () => {
    // Determine stroke based on selection state
    const stroke = strokeColor || (selected ? '#3b82f6' : 'transparent');
    // Increase stroke width slightly when selected for better visibility
    const sw = selected ? Math.max(strokeWidth, 2) : strokeWidth;

    switch (shapeType) {
      // -----------------------------------------------------------------------
      // CIRCLE (actually ellipse to support non-square shapes)
      // -----------------------------------------------------------------------
      case 'circle':
        return (
          <ellipse
            cx={width / 2}                  // Center X
            cy={height / 2}                 // Center Y
            rx={width / 2 - sw}             // Radius X (inset by stroke)
            ry={height / 2 - sw}            // Radius Y (inset by stroke)
            fill={color}
            stroke={stroke}
            strokeWidth={sw}
          />
        );

      // -----------------------------------------------------------------------
      // TRIANGLE (equilateral-ish, pointing up)
      // -----------------------------------------------------------------------
      case 'triangle':
        // Calculate polygon points for a triangle:
        // - Top center: (width/2, strokeWidth)
        // - Bottom right: (width - strokeWidth, height - strokeWidth)
        // - Bottom left: (strokeWidth, height - strokeWidth)
        const points = [
          `${width / 2},${sw}`,              // Top center
          `${width - sw},${height - sw}`,    // Bottom right
          `${sw},${height - sw}`,            // Bottom left
        ].join(' ');
        return (
          <polygon
            points={points}
            fill={color}
            stroke={stroke}
            strokeWidth={sw}
            strokeLinejoin="round"           // Smooth corners
          />
        );

      // -----------------------------------------------------------------------
      // RECTANGLE (default, with rounded corners)
      // -----------------------------------------------------------------------
      case 'rectangle':
      default:
        return (
          <rect
            x={sw / 2}                       // Inset by half stroke
            y={sw / 2}
            width={width - sw}               // Full width minus stroke
            height={height - sw}
            rx={4}                           // Rounded corners (4px radius)
            fill={color}
            stroke={stroke}
            strokeWidth={sw}
          />
        );
    }
  };

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------
  return (
    <>
      {/* ===================================================================
          RESIZE HANDLES
          NodeResizer provides drag-to-resize functionality.
          Only visible when the node is selected.
          =================================================================== */}
      <NodeResizer
        minWidth={40}
        minHeight={40}
        isVisible={selected}
        lineClassName="border-blue-500"
        handleClassName="bg-blue-500 border-2 border-white rounded"
      />

      {/* ===================================================================
          CONNECTION HANDLES
          Handles on all 4 sides allow flexible edge connections.
          =================================================================== */}
      <Handle type="target" position={Position.Top} id="top" className="w-3 h-3" />
      <Handle type="source" position={Position.Bottom} id="bottom" className="w-3 h-3" />
      <Handle type="target" position={Position.Left} id="left" className="w-3 h-3" />
      <Handle type="source" position={Position.Right} id="right" className="w-3 h-3" />

      {/* ===================================================================
          SVG SHAPE CONTAINER
          Wraps the SVG shape with relative positioning for AI badge.
          =================================================================== */}
      <div
        className="relative"
        style={{
          width,
          height,
          // Dynamic shadow based on drag/select state
          boxShadow: getShadowStyle(),
          // Border radius for smoother look
          borderRadius: '8px',
          // Smooth shadow transition + scale effect
          // Matches StickyNoteNode transition timing for consistency
          transition: dragging
            ? 'box-shadow 0.2s cubic-bezier(0.4, 0, 0.2, 1), transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
            : 'box-shadow 0.35s cubic-bezier(0.175, 0.885, 0.32, 1.275), transform 0.35s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
          // Slight scale up when dragging
          transform: dragging ? 'scale(1.02)' : 'scale(1)',
        }}
      >
        {/* -----------------------------------------------------------------
            AI GENERATED BADGE
            Shows sparkle icon when node was created by AI.
            Hover shows timestamp if available.
            ----------------------------------------------------------------- */}
        {data.aiGenerated && (
          <div
            className="absolute top-1 right-1 w-5 h-5 flex items-center justify-center bg-violet-500 rounded-full z-10"
            title={`AI generated${data.model ? ` by ${data.model}` : ''}${data.generatedAt ? ` at ${new Date(data.generatedAt).toLocaleString()}` : ''}`}
          >
            {/* Sparkle icon - SVG for crisp rendering */}
            <svg
              className="w-3 h-3 text-white"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M12 0L14.59 8.41L23 11L14.59 13.59L12 22L9.41 13.59L1 11L9.41 8.41L12 0Z" />
            </svg>
          </div>
        )}

        {/* -----------------------------------------------------------------
            SVG SHAPE
            The actual shape rendering.
            pointer-events-none allows dragging the node by clicking the shape.
            ----------------------------------------------------------------- */}
        <svg
          width={width}
          height={height}
          className="pointer-events-none"      // Clicks pass through to node
          style={{ display: 'block' }}          // Remove inline SVG spacing
        >
          {renderShape()}
        </svg>

        {/* -----------------------------------------------------------------
            TEXT LABEL (if content provided)
            Centered text over the shape.
            ----------------------------------------------------------------- */}
        {data.content && (
          <div
            className="absolute inset-0 flex items-center justify-center pointer-events-none"
            style={{
              padding: '8px',
            }}
          >
            <p
              className="text-sm font-medium text-center break-words"
              style={{
                color: '#000000',  // Black text for readability
                maxWidth: '100%',
                lineHeight: '1.2',
              }}
            >
              {data.content}
            </p>
          </div>
        )}
      </div>
    </>
  );
});

// Display name for React DevTools debugging
ShapeNode.displayName = 'ShapeNode';
